import { useState, useEffect, SyntheticEvent } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { RootState } from 'src/store/reducers';

import * as actions from '@redux-actions/';

import {
    AddressFormStatus,
    GeographicInformationsPayload,
    IAddressForm,
    IBillingAddressForm,
    IAddressDetailsPayload,
    AddressGroupPayload,
} from '@interfaces/myProfileInterfaces';

import {
    FormControl,
    TextField,
    FormHelperText,
    Select,
    MenuItem,
    InputLabel,
    InputAdornment,
    Checkbox,
    FormControlLabel,
} from '@mui/material';

import NumberFormat from 'react-number-format';

import ArrowDown from '@images/svg-icons/arrowDown.svg';
import ErrorIcon from '@images/svg-icons/errorIcon.svg';
import SuccessIcon from '@images/svg-icons/successIcon.svg';
import CloseIcon from '@images/svg-icons/closeIcon.svg';
import PreLoader from '@images/svg-icons/preLoader.svg';

import {
    editAddress,
    getPersonalDetails,
    getPersonalDetailsCityAndState,
} from '@services/myProfileServices';

import { capitalizeFirstLetter, getLabelFallback } from '@utils/utils';
import { replaceLabelPlaceholder } from '@utils/pages-utils/EyeExamsUtils';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { Controller, SubmitHandler, useForm, FieldValues } from 'react-hook-form';

import { isValidZipCode, isValidPhoneNumber } from '@utils/pages-utils/MyProfileUtils';

interface AddressForm extends IAddressForm, IBillingAddressForm { }

interface Props {
    defaultValues?: AddressForm;
    shippingNickName: string;
    billingNickName?: string;
    isDefault: boolean;
}

const EditAddressForm = ({
    defaultValues,
    shippingNickName,
    billingNickName,
    isDefault,
}: Props): JSX.Element => {
    const config = useSelector((state: RootState) => state.common.config);
    const personalDetails = useSelector((state: RootState) => state.myProfile.personalDetails);
    const addressesList = useSelector((state: RootState) => state.myProfile.addressesList);

    useEffect(() => {
        dispatch(getPersonalDetails(config.mockRequest, config.storeId));
        return () => {
            dispatch(actions.getPersonalDetailsSuccess(null));
        };
    }, []);

    const [isUseDefaultPhoneSelected, setIsUseDefaultPhoneSelected] = useState(false);
    const [isShippingAndBillingAddress, setIsShippingAndBillingAddress] = useState(
        !defaultValues.billingAddress1
    );
    const [isDefaultAddress, setIsDefaultAddress] = useState(isDefault);
    const [showAddressLine2, setShowAddressLine2] = useState(!!defaultValues.address2);
    const [showBillingAddressLine2, setShowBillingAddressLine2] = useState(
        !!defaultValues.billingAddress2
    );
    const [groupId, setGroupId] = useState<number>(null);

    useEffect(() => {
        // Calculate the group id for the new address
        const addressNickNamePattern = /^\d+_\d+_\d+$/;

        const groups = addressesList?.contact?.map(address => {
            if (address.nickName.length !== 0 && addressNickNamePattern.test(address.nickName)) {
                return +address.nickName.split('_').at(-1);
            }

            return 0;
        });

        const maxGroup = groups?.length ? Math.max(...groups) : 0;

        setGroupId(maxGroup + 1);
    }, [addressesList]);

    const schema = yup.object().shape({
        firstname: yup
            .string()
            .required(
                replaceLabelPlaceholder(
                    'ADDRESSES_ADDRESS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('ADDRESSES_ADDRESS_FORM_FIRSTNAME_LABEL')
                )
            )
            .test('no-numbers', getLabelFallback('ADDRESSES_ADDRESS_FORM_INVALID_FIRSTNAME'), val =>
                val ? !/\d/.test(val) : true
            ),
        lastname: yup
            .string()
            .required(
                replaceLabelPlaceholder(
                    'ADDRESSES_ADDRESS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('ADDRESSES_ADDRESS_FORM_LASTNAME_LABEL')
                )
            )
            .test('no-numbers', getLabelFallback('ADDRESSES_ADDRESS_FORM_INVALID_LASTNAME'), val =>
                val ? !/\d/.test(val) : true
            ),
        address1: yup
            .string()
            .required(
                replaceLabelPlaceholder(
                    'ADDRESSES_ADDRESS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('ADDRESSES_ADDRESS_FORM_ADDRESS1_LABEL')
                )
            ),
        address2: yup.string(),
        zipcode: yup
            .string()
            .test(
                'len',
                getLabelFallback(
                    `${config.countryName === 'CA'
                        ? 'ADDRESSES_ADDRESS_FORM_POSTAL_CODE_ERROR_MESSAGE'
                        : 'ADDRESSES_ADDRESS_FORM_ZIP_CODE_ERROR_MESSAGE'
                    }`
                ),
                val => (val ? isValidZipCode(val.toString(), config) : true)
            )
            .required(
                replaceLabelPlaceholder(
                    'ADDRESSES_ADDRESS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback(
                        `${config.countryName === 'CA'
                            ? 'ADDRESSES_ADDRESS_FORM_ZIPCODE_CA_LABEL'
                            : 'ADDRESSES_ADDRESS_FORM_ZIPCODE_LABEL'
                        }`
                    ).toLowerCase()
                )
            ),
        city: yup
            .string()
            .required(
                replaceLabelPlaceholder(
                    'ADDRESSES_ADDRESS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('ADDRESSES_ADDRESS_FORM_CITY_LABEL')
                )
            ),
        state: yup
            .string()
            .required(
                replaceLabelPlaceholder(
                    'ADDRESSES_ADDRESS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('ADDRESSES_ADDRESS_FORM_STATE_LABEL')
                )
            ),
        billingFirstname: yup.string().when('_', (_, schema) => {
            if (!isShippingAndBillingAddress) {
                return schema
                    .required(
                        replaceLabelPlaceholder(
                            'ADDRESSES_ADDRESS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                            '{FORM_FIELD}',
                            getLabelFallback('ADDRESSES_ADDRESS_FORM_FIRSTNAME_LABEL')
                        )
                    )
                    .test(
                        'no-numbers',
                        getLabelFallback('ADDRESSES_ADDRESS_FORM_INVALID_FIRSTNAME'),
                        val => (val ? !/\d/.test(val) : true)
                    );
            }
            return schema;
        }),
        billingLastname: yup.string().when('_', (_, schema) => {
            if (!isShippingAndBillingAddress) {
                return schema
                    .required(
                        replaceLabelPlaceholder(
                            'ADDRESSES_ADDRESS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                            '{FORM_FIELD}',
                            getLabelFallback('ADDRESSES_ADDRESS_FORM_LASTNAME_LABEL')
                        )
                    )
                    .test(
                        'no-numbers',
                        getLabelFallback('ADDRESSES_ADDRESS_FORM_INVALID_LASTNAME'),
                        val => (val ? !/\d/.test(val) : true)
                    );
            }
            return schema;
        }),
        billingAddress1: yup.string().when('_', (_, schema) => {
            if (!isShippingAndBillingAddress) {
                return schema.required(
                    replaceLabelPlaceholder(
                        'ADDRESSES_ADDRESS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                        '{FORM_FIELD}',
                        getLabelFallback('ADDRESSES_ADDRESS_FORM_ADDRESS1_LABEL')
                    )
                );
            }
            return schema;
        }),
        billingAddress2: yup.string(),
        billingZipcode: yup.string().when('_', (_, schema) => {
            if (!isShippingAndBillingAddress) {
                return schema
                    .test(
                        'len',
                        getLabelFallback(
                            `${config.countryName === 'CA'
                                ? 'ADDRESSES_ADDRESS_FORM_POSTAL_CODE_ERROR_MESSAGE'
                                : 'ADDRESSES_ADDRESS_FORM_ZIP_CODE_ERROR_MESSAGE'
                            }`
                        ),
                        val => (val ? isValidZipCode(val.toString(), config) : true)
                    )
                    .required(
                        replaceLabelPlaceholder(
                            'ADDRESSES_ADDRESS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                            '{FORM_FIELD}',
                            getLabelFallback(
                                `${config.countryName === 'CA'
                                    ? 'ADDRESSES_ADDRESS_FORM_ZIPCODE_CA_LABEL'
                                    : 'ADDRESSES_ADDRESS_FORM_ZIPCODE_LABEL'
                                }`
                            ).toLowerCase()
                        )
                    );
            }
            return schema;
        }),
        billingCity: yup.string().when('_', (_, schema) => {
            if (!isShippingAndBillingAddress) {
                return schema.required(
                    replaceLabelPlaceholder(
                        'ADDRESSES_ADDRESS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                        '{FORM_FIELD}',
                        getLabelFallback('ADDRESSES_ADDRESS_FORM_CITY_LABEL')
                    )
                );
            }
            return schema;
        }),
        billingState: yup.string().when('_', (_, schema) => {
            if (!isShippingAndBillingAddress) {
                return schema.required(
                    replaceLabelPlaceholder(
                        'ADDRESSES_ADDRESS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                        '{FORM_FIELD}',
                        getLabelFallback('ADDRESSES_ADDRESS_FORM_STATE_LABEL')
                    )
                );
            }
            return schema;
        }),
        phoneNumber: yup
            .string()
            .required(
                replaceLabelPlaceholder(
                    'ADDRESSES_ADDRESS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('ADDRESSES_ADDRESS_FORM_PHONENUMBER_LABEL')
                )
            )
            .test(
                'valid-phone',
                getLabelFallback('ADDRESSES_ADDRESS_FORM_INVALID_PHONENUMBER'),
                isValidPhoneNumber
            ),
    });

    const {
        register,
        watch,
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
        clearErrors,
        setError,
        setValue,
    } = useForm<AddressForm>({
        resolver: yupResolver(schema),
        defaultValues: defaultValues as FieldValues,
    });

    const watchPhoneNumber = watch('phoneNumber');
    const watchZipCode = watch('zipcode');
    const watchCity = watch('city');
    const watchBillingZipCode = watch('billingZipcode');
    const watchBillingCity = watch('billingCity');

    const initialFormStatus: AddressFormStatus<Partial<AddressForm>> = {
        firstname: {
            focus: false,
            success: false,
        },
        lastname: {
            focus: false,
            success: false,
        },
        address1: {
            focus: false,
            success: false,
        },
        address2: {
            focus: false,
            success: false,
        },
        zipcode: {
            focus: false,
            success: false,
        },
        city: {
            focus: false,
            success: false,
        },
        state: {
            focus: false,
            success: false,
        },
        billingFirstname: {
            focus: false,
            success: false,
        },
        billingLastname: {
            focus: false,
            success: false,
        },
        billingAddress1: {
            focus: false,
            success: false,
        },
        billingAddress2: {
            focus: false,
            success: false,
        },
        billingZipcode: {
            focus: false,
            success: false,
        },
        billingCity: {
            focus: false,
            success: false,
        },
        billingState: {
            focus: false,
            success: false,
        },
        phoneNumber: {
            focus: false,
            success: false,
        },
    };

    const [formFieldStatus, setFormFieldStatus] =
        useState<AddressFormStatus<Partial<AddressForm>>>(initialFormStatus);

    const dispatch = useDispatch();

    const addPaymentMethodRequestStatus = useSelector(
        (state: RootState) => state.myProfile.addPaymentMethodRequestStatus
    );

    const geographicInformations = useSelector(
        (state: RootState) => state.myProfile.geographicInformations
    );

    useEffect(() => {
        setValue(
            'phoneNumber',
            isUseDefaultPhoneSelected ? personalDetails.phone1 : defaultValues.phoneNumber
        );

        if (
            !!personalDetails &&
            !!personalDetails.phone1
        ) {
            if (isUseDefaultPhoneSelected && isValidPhoneNumber(personalDetails.phone1)) {
                clearErrors('phoneNumber');
                setFormFieldStatus({
                    ...formFieldStatus,
                    'phoneNumber': {
                        success: true,
                        focus: false,
                    },
                });
            } else {
                setFormFieldStatus({
                    ...formFieldStatus,
                    'phoneNumber': {
                        success: false,
                        focus: false,
                    },
                });
            }
        }
    }, [isUseDefaultPhoneSelected]);

    useEffect(() => {
        if (geographicInformations) {
            const { city, state } = geographicInformations;
            city ? (setValue('city', city), clearErrors('city')) : null;
            state ? (setValue('state', state), clearErrors('state')) : null;
            setFormFieldStatus({
                ...formFieldStatus,
                ...(city && { city: { success: true, focus: false } }),
                ...(state && { state: { success: true, focus: false } }),
            });
        }
        return () => {
            dispatch(actions.getGeographicInformationsSuccess(null));
        };
    }, [geographicInformations]);

    /** The function below handles the onBlur, onChange and onFocus events for the form validation */
    const handleInputEvent = (e: SyntheticEvent) => {
        e.persist();
        const target = e.target as HTMLInputElement;

        if (!isSubmitting) clearErrors(target.name as keyof AddressForm);

        if (target.name === 'zipcode') {
            // TODO: autocomplete for billing zipcode field
            if (e.type === 'change') {
                setValue(
                    target.name as keyof IAddressForm,
                    target.value.slice(0, target.maxLength)
                );
            }

            if (e.type === 'blur') {
                const geographicInfoPayload: GeographicInformationsPayload = {
                    isMockRequest: config.mockRequest,
                    zipCode: target.value,
                    addressType: 'other',
                };
                target.value.length < target.maxLength ||
                    (config.countryName === 'CA' &&
                        !/^[A-Za-z]\d[A-Za-z][ ]\d[A-Za-z]\d$/.test(target.value))
                    ? (setFormFieldStatus({
                        ...formFieldStatus,
                        [target.name]: { success: false, focus: false },
                    }),
                        setValue(target.name, target.value),
                        setError(target.name as keyof IAddressForm, {
                            type: 'maxLength',
                            message: getLabelFallback(
                                `${config.countryName === 'CA'
                                    ? 'ADDRESSES_ADDRESS_FORM_POSTAL_CODE_ERROR_MESSAGE'
                                    : 'ADDRESSES_ADDRESS_FORM_ZIP_CODE_ERROR_MESSAGE'
                                }`
                            ),
                        }))
                    : dispatch(getPersonalDetailsCityAndState(geographicInfoPayload));
            }
        }

        if (
            ['firstname', 'lastname', 'address1', 'city', 'billingFirstname', 'billingLastname', 'billingAddress1', 'billingCity'].includes(target.name) &&
            e.type == 'blur' &&
            target.value.length &&
            target.value.trim() === ''
        ) {
            return (
                setFormFieldStatus({
                    ...formFieldStatus,
                    [target.name]: {
                        success: false,
                        focus: false,
                    },
                }),
                setError(target.name as keyof IAddressForm, {
                    type: 'pattern',
                    message: getLabelFallback(
                        `ADDRESSES_ADDRESS_FORM_INVALID_${target.name.toUpperCase()}`
                    ),
                })
            );
        }

        if (
            ['firstname', 'lastname'].includes(target.name) &&
            e.type == 'blur' &&
            target.value.length &&
            /\d/.test(target.value)
        ) {
            return (
                setFormFieldStatus({
                    ...formFieldStatus,
                    [target.name]: {
                        success: false,
                        focus: false,
                    },
                }),
                setError(target.name as keyof IAddressForm, {
                    type: 'pattern',
                    message: getLabelFallback(
                        `ADDRESSES_ADDRESS_FORM_INVALID_${target.name.toUpperCase()}`
                    ),
                })
            );
        }

        if (
            target.name === 'phoneNumber' &&
            e.type == 'blur' &&
            !isValidPhoneNumber(target.value)
        ) {
            return (
                setFormFieldStatus({
                    ...formFieldStatus,
                    [target.name]: {
                        success: false,
                        focus: false,
                    },
                }),
                setError(target.name as keyof IAddressForm, {
                    type: 'pattern',
                    message: getLabelFallback(
                        `ADDRESSES_ADDRESS_FORM_INVALID_${target.name.toUpperCase()}`
                    ),
                })
            );
        }

        if (target.name === 'address2') return; // optional field, no validation needed

        target.value
            ? (setFormFieldStatus({
                ...formFieldStatus,
                [target.name]: {
                    success: e.type === 'blur' ? true : e.type === 'change' ? false : false,
                    focus:
                        e.type === 'blur'
                            ? false
                            : e.type === 'change'
                                ? true
                                : e.type === 'focus' && !errors[target.name]
                                    ? true
                                    : false,
                },
            }),
                setValue(target.name as keyof AddressForm, target.value))
            : (setValue(target.name as keyof AddressForm, ''),
                setFormFieldStatus({
                    ...formFieldStatus,
                    [target.name]: { success: false, focus: false },
                }),
                e.type === 'blur' &&
                setError(target.name as keyof AddressForm, {
                    type: 'required',
                    message: replaceLabelPlaceholder(
                        'ADDRESSES_ADDRESS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                        '{FORM_FIELD}',
                        getLabelFallback(
                            `ADDRESSES_ADDRESS_FORM_${target.name.toLocaleUpperCase()}_LABEL`
                        )
                    ),
                }));
    };

    const onSubmit: SubmitHandler<AddressForm> = (data: AddressForm) => {
        const now = new Date();
        const toTimestamp = (date: Date, offset = 0) => {
            return new Date(date.getTime() + offset).toISOString().replace(/\D/g, '');
        };
        const nicknameTemplate = '{userId}_{timestamp}_{group}'
            .replace('{userId}', config.userId)
            .replace('{group}', shippingNickName.split('_').at(-1));

        const dataToSend = {} as AddressGroupPayload; // map of (nickName, address) pairs

        const phoneNumber = isUseDefaultPhoneSelected ? personalDetails.phone1 : data.phoneNumber;

        const shippingAddress = {
            addressLine: [data.address1, data.address2 || ''],
            addressType: isShippingAndBillingAddress ? 'SB' : 'S',
            city: data.city,
            country: 'US',
            email1: personalDetails.email,
            firstName: data.firstname,
            lastName: data.lastname,
            nickName: shippingNickName,
            phone1: phoneNumber,
            phone1Type: 'CEL',
            primary: isDefaultAddress ? 'true' : 'false',
            state: data.state,
            zipCode: data.zipcode,
        } as IAddressDetailsPayload;

        dataToSend[shippingNickName] = {
            contact: [shippingAddress],
        };

        // there's a separate billing address
        if (!isShippingAndBillingAddress) {
            const bNickName = billingNickName
                ? billingNickName
                : nicknameTemplate.replace('{timestamp}', toTimestamp(now, 1));

            const billingAddress = {
                addressLine: [data.billingAddress1, data.billingAddress2 || ''],
                addressType: 'B',
                city: data.billingCity,
                country: 'US',
                email1: personalDetails.email,
                firstName: data.billingFirstname,
                lastName: data.billingLastname,
                nickName: bNickName,
                phone1: phoneNumber,
                phone1Type: 'CEL',
                primary: isDefaultAddress ? 'true' : 'false',
                state: data.billingState,
                zipCode: data.billingZipcode,
            } as IAddressDetailsPayload;

            dataToSend[bNickName] = {
                contact: [billingAddress],
            };
        }

        console.log(dataToSend);
        dispatch(editAddress(config.mockRequest, config.storeId, dataToSend));
    };

    return (
        <div className="content-container address-form">
            <span className="address-form-title" role="region" aria-label="address form title">
                {getLabelFallback('ADDRESSES_ADDRESS_FORM_TITLE')}
            </span>

            <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
                <div className="form-group form-group_name">
                    <TextField
                        {...register('firstname')}
                        className={`${errors?.firstname?.message
                            ? 'form-field-error'
                            : formFieldStatus.firstname.success
                                ? 'form-field-success'
                                : ''
                            }`}
                        onChange={handleInputEvent}
                        onFocus={handleInputEvent}
                        onBlur={handleInputEvent}
                        name="firstname"
                        variant="outlined"
                        label={capitalizeFirstLetter(
                            getLabelFallback('ADDRESSES_ADDRESS_FORM_FIRSTNAME_LABEL_REQUIRED')
                        )}
                        helperText={errors?.firstname?.message}
                        InputProps={{
                            endAdornment: formFieldStatus.firstname.focus ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <CloseIcon
                                        className="cursor-pointer"
                                        onMouseDown={() => setValue('firstname', '')}
                                    />
                                </InputAdornment>
                            ) : errors?.firstname?.message ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <ErrorIcon />
                                </InputAdornment>
                            ) : formFieldStatus.firstname.success ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <SuccessIcon />
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                    <TextField
                        {...register('lastname')}
                        className={`${errors?.lastname?.message
                            ? 'form-field-error'
                            : formFieldStatus.lastname.success
                                ? 'form-field-success'
                                : ''
                            }`}
                        onChange={handleInputEvent}
                        onFocus={handleInputEvent}
                        onBlur={handleInputEvent}
                        name="lastname"
                        variant="outlined"
                        label={capitalizeFirstLetter(
                            getLabelFallback('ADDRESSES_ADDRESS_FORM_LASTNAME_LABEL_REQUIRED')
                        )}
                        helperText={errors?.lastname?.message}
                        InputProps={{
                            endAdornment: formFieldStatus.lastname.focus ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <CloseIcon
                                        className="cursor-pointer"
                                        onMouseDown={() => setValue('lastname', '')}
                                    />
                                </InputAdornment>
                            ) : errors?.lastname?.message ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <ErrorIcon />
                                </InputAdornment>
                            ) : formFieldStatus.lastname.success ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <SuccessIcon />
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                </div>

                {
                    <div className="form-group form-group_phone-number">
                        {(
                            <NumberFormat
                                {...register('phoneNumber')}
                                type="tel"
                                format="(+1) ###-###-####"
                                mask="  "
                                customInput={TextField}
                                className={`${errors?.phoneNumber?.message
                                    ? 'form-field-error'
                                    : formFieldStatus.phoneNumber.success
                                        ? 'form-field-success'
                                        : ''
                                    }`}
                                name="phoneNumber"
                                variant="outlined"
                                value={watchPhoneNumber}
                                onChange={handleInputEvent}
                                onFocus={handleInputEvent}
                                onBlur={handleInputEvent}
                                defaultValue={watchPhoneNumber}
                                label={capitalizeFirstLetter(
                                    getLabelFallback('ADDRESSES_ADDRESS_FORM_PHONENUMBER_LABEL_REQUIRED')
                                )}
                                helperText={errors?.phoneNumber?.message}
                                FormHelperTextProps={{
                                    classes: {
                                        root: 'full-width',
                                    },
                                }}
                                InputProps={{
                                    endAdornment: formFieldStatus.phoneNumber.focus ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <CloseIcon
                                                className="cursor-pointer"
                                                onMouseDown={() => setValue('phoneNumber', '')}
                                            />
                                        </InputAdornment>
                                    ) : errors?.phoneNumber?.message ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <ErrorIcon />
                                        </InputAdornment>
                                    ) : formFieldStatus.phoneNumber.success ? (
                                        <InputAdornment position="end">
                                            <SuccessIcon />
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                        )}

                        {personalDetails && personalDetails.phone1 && (
                            <FormControlLabel
                                data-analytics_available_call="0"
                                className="checkbox"
                                classes={{ label: 'primary-message medium' }}
                                control={
                                    <Checkbox
                                        size="small"
                                        defaultChecked={false}
                                        onClick={() =>
                                            setIsUseDefaultPhoneSelected(!isUseDefaultPhoneSelected)
                                        }
                                    />
                                }
                                label={getLabelFallback(
                                    'ADDRESSES_ADDRESS_FORM_PHONENUMBER_USE_DEFAULT_CHECKBOX'
                                )}
                            />
                        )}
                    </div>
                }

                <div className="form-group form-group_address">
                    <TextField
                        {...register('address1')}
                        className={`${errors?.address1?.message
                            ? 'form-field-error'
                            : formFieldStatus.address1.success
                                ? 'form-field-success'
                                : ''
                            }`}
                        onChange={handleInputEvent}
                        onFocus={handleInputEvent}
                        onBlur={handleInputEvent}
                        name="address1"
                        variant="outlined"
                        label={capitalizeFirstLetter(
                            getLabelFallback('ADDRESSES_ADDRESS_FORM_ADDRESS1_LABEL_REQUIRED')
                        )}
                        helperText={
                            errors?.address1?.message ||
                            getLabelFallback('ADDRESSES_ADDRESS_FORM_ADDRESS1_HELPER_TEXT')
                        }
                        InputProps={{
                            endAdornment: formFieldStatus.address1.focus ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <CloseIcon
                                        className="cursor-pointer"
                                        onMouseDown={() => setValue('address1', '')}
                                    />
                                </InputAdornment>
                            ) : errors?.address1?.message ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <ErrorIcon />
                                </InputAdornment>
                            ) : formFieldStatus.address1.success ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <SuccessIcon />
                                </InputAdornment>
                            ) : null,
                        }}
                    />

                    {showAddressLine2 ? (
                        <TextField
                            {...register('address2')}
                            className={`${errors?.address2?.message
                                ? 'form-field-error'
                                : formFieldStatus.address1.success
                                    ? 'form-field-success'
                                    : ''
                                }`}
                            onChange={handleInputEvent}
                            onFocus={handleInputEvent}
                            onBlur={handleInputEvent}
                            name="address2"
                            variant="outlined"
                            label={capitalizeFirstLetter(
                                getLabelFallback('ADDRESSES_ADDRESS_FORM_ADDRESS2_LABEL')
                            )}
                            helperText={
                                errors?.address2?.message ||
                                getLabelFallback('ADDRESSES_ADDRESS_FORM_ADDRESS2_HELPER_TEXT')
                            }
                            InputProps={{
                                endAdornment: formFieldStatus.address2.focus ? (
                                    <InputAdornment position="end" sx={{ width: '25px' }}>
                                        <CloseIcon
                                            className="cursor-pointer"
                                            onMouseDown={() => setValue('address1', '')}
                                        />
                                    </InputAdornment>
                                ) : errors?.address2?.message ? (
                                    <InputAdornment position="end" sx={{ width: '25px' }}>
                                        <ErrorIcon />
                                    </InputAdornment>
                                ) : formFieldStatus.address2.success ? (
                                    <InputAdornment position="end" sx={{ width: '25px' }}>
                                        <SuccessIcon />
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                    ) : (
                        <button className="link-button" onClick={() => setShowAddressLine2(true)}>
                            {getLabelFallback('ADDRESSES_ADDRESS_FORM_SHOW_ADDRESS2_LABEL')}
                        </button>
                    )}
                </div>

                <div className="form-group form-group_zip-code">
                    <TextField
                        {...register('zipcode')}
                        className={`medium-input-field ${errors?.zipcode?.message
                            ? 'form-field-error'
                            : formFieldStatus.zipcode.success
                                ? 'form-field-success'
                                : ''
                            }`}
                        onChange={handleInputEvent}
                        onFocus={handleInputEvent}
                        onBlur={handleInputEvent}
                        name="zipcode"
                        variant="outlined"
                        type={`${config.countryName.toUpperCase() === 'CA' ? 'text' : 'number'}`}
                        label={getLabelFallback(
                            `${config.countryName === 'CA'
                                ? 'ADDRESSES_ADDRESS_FORM_ZIPCODE_CA_LABEL'
                                : 'ADDRESSES_ADDRESS_FORM_ZIPCODE_LABEL_REQUIRED'
                            }`
                        )}
                        defaultValue={watchZipCode || null}
                        helperText={errors?.zipcode?.message}
                        inputProps={{
                            minLength: `${config.countryName.toUpperCase() === 'CA' ? 7 : 5}`,
                            maxLength: `${config.countryName.toUpperCase() === 'CA' ? 7 : 5}`,
                        }}
                        InputProps={{
                            endAdornment: formFieldStatus.zipcode.focus ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <CloseIcon
                                        className="cursor-pointer"
                                        onMouseDown={() => setValue('zipcode', '')}
                                    />
                                </InputAdornment>
                            ) : errors?.zipcode?.message ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <ErrorIcon />
                                </InputAdornment>
                            ) : formFieldStatus.zipcode.success ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <SuccessIcon />
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                </div>

                <div className="form-group form-group_city">
                    <TextField
                        {...register('city')}
                        className={`${errors?.city?.message
                            ? 'form-field-error'
                            : formFieldStatus.city.success
                                ? 'form-field-success'
                                : ''
                            }`}
                        onChange={handleInputEvent}
                        onFocus={handleInputEvent}
                        onBlur={handleInputEvent}
                        name="city"
                        variant="outlined"
                        value={watchCity || ''}
                        label={capitalizeFirstLetter(
                            getLabelFallback('ADDRESSES_ADDRESS_FORM_CITY_LABEL_REQUIRED')
                        )}
                        helperText={errors?.city?.message}
                        InputProps={{
                            endAdornment: formFieldStatus.city.focus ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <CloseIcon
                                        className="cursor-pointer"
                                        onMouseDown={() => setValue('city', '')}
                                    />
                                </InputAdornment>
                            ) : errors?.city?.message ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <ErrorIcon />
                                </InputAdornment>
                            ) : formFieldStatus.city.success ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <SuccessIcon />
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                    <Controller
                        name="state"
                        control={control}
                        defaultValue=""
                        render={({ field: { value }, fieldState, field }) => (
                            <FormControl
                                className={` ${fieldState?.error?.message
                                    ? 'form-field-error'
                                    : value
                                        ? 'form-field-success'
                                        : ''
                                    }`}
                            >
                                <InputLabel id="billing-state-label">
                                    {capitalizeFirstLetter(
                                        getLabelFallback('ADDRESSES_ADDRESS_FORM_STATE_LABEL_REQUIRED')
                                    )}
                                </InputLabel>
                                <Select
                                    name="state"
                                    labelId="billing-state-label"
                                    variant="outlined"
                                    IconComponent={props => <ArrowDown {...props} />}
                                    label={capitalizeFirstLetter(
                                        getLabelFallback('ADDRESSES_ADDRESS_FORM_STATE_LABEL_REQUIRED')
                                    )}
                                    endAdornment={
                                        fieldState?.error?.message ? (
                                            <InputAdornment
                                                position="end"
                                                sx={{
                                                    width: '25px',
                                                    position: 'absolute',
                                                    right: '45px',
                                                }}
                                            >
                                                <ErrorIcon />
                                            </InputAdornment>
                                        ) : value ? (
                                            <InputAdornment
                                                position="end"
                                                sx={{
                                                    width: '25px',
                                                    position: 'absolute',
                                                    right: '45px',
                                                }}
                                            >
                                                <SuccessIcon />
                                            </InputAdornment>
                                        ) : null
                                    }
                                    {...field}
                                >
                                    {config.stateList.map(option => (
                                        <MenuItem
                                            key={option.code}
                                            value={option.code}
                                            disabled={option.disabled}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors?.state?.message && (
                                    <FormHelperText className="error-helper-text">
                                        {errors?.state?.message}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        )}
                    />
                </div>

                <div className="form-group form-group_is-billing">
                    <FormControlLabel
                        data-analytics_available_call="0"
                        className="checkbox"
                        classes={{ label: 'primary-message medium' }}
                        control={
                            <Checkbox
                                size="small"
                                checked={isShippingAndBillingAddress}
                                onClick={() =>
                                    setIsShippingAndBillingAddress(!isShippingAndBillingAddress)
                                }
                            />
                        }
                        label={getLabelFallback('ADDRESSES_ADDRESS_FORM_ADDRESS_USE_BILLING_LABEL')}
                    />
                </div>

                <div className="form-group form-group_is-default">
                    <FormControlLabel
                        data-analytics_available_call="0"
                        className="checkbox"
                        classes={{ label: 'primary-message medium' }}
                        control={
                            <Checkbox
                                size="small"
                                checked={isDefaultAddress}
                                onClick={() => setIsDefaultAddress(!isDefaultAddress)}
                            />
                        }
                        label={getLabelFallback('ADDRESSES_ADDRESS_FORM_ADDRESS_USE_DEFAULT_LABEL')}
                    />
                </div>

                {/* part of the form that adds a different billing address */}

                {!isShippingAndBillingAddress && (
                    <fieldset id="billing" name="billing">
                        <span className="address-form-title" role="region">
                            {getLabelFallback('ADDRESSES_ADDRESS_FORM_BILLING_TITLE')}
                        </span>

                        <div className="form-group form-group_billing-name">
                            <TextField
                                {...register('billingFirstname')}
                                className={`${errors?.billingFirstname?.message
                                    ? 'form-field-error'
                                    : formFieldStatus.billingFirstname.success
                                        ? 'form-field-success'
                                        : ''
                                    }`}
                                onChange={handleInputEvent}
                                onFocus={handleInputEvent}
                                onBlur={handleInputEvent}
                                name="billingFirstname"
                                variant="outlined"
                                label={capitalizeFirstLetter(
                                    getLabelFallback('ADDRESSES_ADDRESS_FORM_FIRSTNAME_LABEL_REQUIRED')
                                )}
                                helperText={errors?.billingFirstname?.message}
                                InputProps={{
                                    endAdornment: formFieldStatus.billingFirstname.focus ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <CloseIcon
                                                className="cursor-pointer"
                                                onMouseDown={() => setValue('billingFirstname', '')}
                                            />
                                        </InputAdornment>
                                    ) : errors?.billingFirstname?.message ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <ErrorIcon />
                                        </InputAdornment>
                                    ) : formFieldStatus.billingFirstname.success ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <SuccessIcon />
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                            <TextField
                                {...register('billingLastname')}
                                className={`${errors?.billingLastname?.message
                                    ? 'form-field-error'
                                    : formFieldStatus.billingLastname.success
                                        ? 'form-field-success'
                                        : ''
                                    }`}
                                onChange={handleInputEvent}
                                onFocus={handleInputEvent}
                                onBlur={handleInputEvent}
                                name="billingLastname"
                                variant="outlined"
                                label={capitalizeFirstLetter(
                                    getLabelFallback('ADDRESSES_ADDRESS_FORM_LASTNAME_LABEL_REQUIRED')
                                )}
                                helperText={errors?.billingLastname?.message}
                                InputProps={{
                                    endAdornment: formFieldStatus.billingLastname.focus ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <CloseIcon
                                                className="cursor-pointer"
                                                onMouseDown={() => setValue('billingLastname', '')}
                                            />
                                        </InputAdornment>
                                    ) : errors?.billingLastname?.message ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <ErrorIcon />
                                        </InputAdornment>
                                    ) : formFieldStatus.billingLastname.success ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <SuccessIcon />
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                        </div>

                        <div className="form-group form-group_billing-address">
                            <TextField
                                {...register('billingAddress1')}
                                className={`${errors?.billingAddress1?.message
                                    ? 'form-field-error'
                                    : formFieldStatus.billingAddress1.success
                                        ? 'form-field-success'
                                        : ''
                                    }`}
                                onChange={handleInputEvent}
                                onFocus={handleInputEvent}
                                onBlur={handleInputEvent}
                                name="billingAddress1"
                                variant="outlined"
                                label={capitalizeFirstLetter(
                                    getLabelFallback('ADDRESSES_ADDRESS_FORM_BILLINGADDRESS1_LABEL_REQUIRED')
                                )}
                                helperText={errors?.billingAddress1?.message}
                                InputProps={{
                                    endAdornment: formFieldStatus.billingAddress1.focus ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <CloseIcon
                                                className="cursor-pointer"
                                                onMouseDown={() => setValue('billingAddress1', '')}
                                            />
                                        </InputAdornment>
                                    ) : errors?.billingAddress1?.message ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <ErrorIcon />
                                        </InputAdornment>
                                    ) : formFieldStatus.billingAddress1.success ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <SuccessIcon />
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />

                            {showBillingAddressLine2 ? (
                                <TextField
                                    {...register('billingAddress2')}
                                    className={`${errors?.billingAddress2?.message
                                        ? 'form-field-error'
                                        : formFieldStatus.address1.success
                                            ? 'form-field-success'
                                            : ''
                                        }`}
                                    onChange={handleInputEvent}
                                    onFocus={handleInputEvent}
                                    onBlur={handleInputEvent}
                                    name="billingAddress2"
                                    variant="outlined"
                                    label={capitalizeFirstLetter(
                                        getLabelFallback('ADDRESSES_ADDRESS_FORM_ADDRESS2_LABEL')
                                    )}
                                    helperText={
                                        errors?.billingAddress2?.message ||
                                        getLabelFallback(
                                            'ADDRESSES_ADDRESS_FORM_ADDRESS2_HELPER_TEXT'
                                        )
                                    }
                                    InputProps={{
                                        endAdornment: formFieldStatus.billingAddress2.focus ? (
                                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                                <CloseIcon
                                                    className="cursor-pointer"
                                                    onMouseDown={() => setValue('address1', '')}
                                                />
                                            </InputAdornment>
                                        ) : errors?.billingAddress2?.message ? (
                                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                                <ErrorIcon />
                                            </InputAdornment>
                                        ) : formFieldStatus.billingAddress2.success ? (
                                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                                <SuccessIcon />
                                            </InputAdornment>
                                        ) : null,
                                    }}
                                />
                            ) : (
                                <button
                                    className="link-button"
                                    onClick={() => setShowBillingAddressLine2(true)}
                                >
                                    {getLabelFallback('ADDRESSES_ADDRESS_FORM_SHOW_ADDRESS2_LABEL')}
                                </button>
                            )}
                        </div>

                        <div className="form-group form-group_billing-zip-code">
                            <TextField
                                {...register('billingZipcode')}
                                className={`medium-input-field ${errors?.billingZipcode?.message
                                    ? 'form-field-error'
                                    : formFieldStatus.billingZipcode.success
                                        ? 'form-field-success'
                                        : ''
                                    }`}
                                onChange={handleInputEvent}
                                onFocus={handleInputEvent}
                                onBlur={handleInputEvent}
                                name="billingZipcode"
                                variant="outlined"
                                type={`${config.countryName.toUpperCase() === 'CA' ? 'text' : 'number'
                                    }`}
                                label={getLabelFallback(
                                    `${config.countryName === 'CA'
                                        ? 'ADDRESSES_ADDRESS_FORM_ZIPCODE_CA_LABEL'
                                        : 'ADDRESSES_ADDRESS_FORM_ZIPCODE_LABEL_REQUIRED'
                                    }`
                                )}
                                defaultValue={watchBillingZipCode || null}
                                helperText={errors?.billingZipcode?.message}
                                inputProps={{
                                    minLength: `${config.countryName.toUpperCase() === 'CA' ? 7 : 5
                                        }`,
                                    maxLength: `${config.countryName.toUpperCase() === 'CA' ? 7 : 5
                                        }`,
                                }}
                                InputProps={{
                                    endAdornment: formFieldStatus.billingZipcode.focus ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <CloseIcon
                                                className="cursor-pointer"
                                                onMouseDown={() => setValue('billingZipcode', '')}
                                            />
                                        </InputAdornment>
                                    ) : errors?.billingZipcode?.message ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <ErrorIcon />
                                        </InputAdornment>
                                    ) : formFieldStatus.billingZipcode.success ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <SuccessIcon />
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                        </div>

                        <div className="form-group form-group_billing-city">
                            <TextField
                                {...register('billingCity')}
                                className={`${errors?.billingCity?.message
                                    ? 'form-field-error'
                                    : formFieldStatus.billingCity.success
                                        ? 'form-field-success'
                                        : ''
                                    }`}
                                onChange={handleInputEvent}
                                onFocus={handleInputEvent}
                                onBlur={handleInputEvent}
                                name="billingCity"
                                variant="outlined"
                                value={watchBillingCity || ''}
                                label={capitalizeFirstLetter(
                                    getLabelFallback('ADDRESSES_ADDRESS_FORM_BILLINGCITY_LABEL_REQUIRED')
                                )}
                                helperText={errors?.billingCity?.message}
                                InputProps={{
                                    endAdornment: formFieldStatus.billingCity.focus ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <CloseIcon
                                                className="cursor-pointer"
                                                onMouseDown={() => setValue('billingCity', '')}
                                            />
                                        </InputAdornment>
                                    ) : errors?.billingCity?.message ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <ErrorIcon />
                                        </InputAdornment>
                                    ) : formFieldStatus.billingCity.success ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <SuccessIcon />
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                            <Controller
                                name="billingState"
                                control={control}
                                defaultValue=""
                                render={({ field: { value }, fieldState, field }) => (
                                    <FormControl
                                        className={` ${fieldState?.error?.message
                                            ? 'form-field-error'
                                            : value
                                                ? 'form-field-success'
                                                : ''
                                            }`}
                                    >
                                        <InputLabel id="billing-state-label">
                                            {capitalizeFirstLetter(
                                                getLabelFallback(
                                                    'ADDRESSES_ADDRESS_FORM_BILLINGSTATE_LABEL_REQUIRED'
                                                )
                                            )}
                                        </InputLabel>
                                        <Select
                                            name="billingState"
                                            labelId="billing-state-label"
                                            variant="outlined"
                                            IconComponent={props => <ArrowDown {...props} />}
                                            label={capitalizeFirstLetter(
                                                getLabelFallback(
                                                    'ADDRESSES_ADDRESS_FORM_BILLINGSTATE_LABEL_REQUIRED'
                                                )
                                            )}
                                            endAdornment={
                                                fieldState?.error?.message ? (
                                                    <InputAdornment
                                                        position="end"
                                                        sx={{
                                                            width: '25px',
                                                            position: 'absolute',
                                                            right: '45px',
                                                        }}
                                                    >
                                                        <ErrorIcon />
                                                    </InputAdornment>
                                                ) : value ? (
                                                    <InputAdornment
                                                        position="end"
                                                        sx={{
                                                            width: '25px',
                                                            position: 'absolute',
                                                            right: '45px',
                                                        }}
                                                    >
                                                        <SuccessIcon />
                                                    </InputAdornment>
                                                ) : null
                                            }
                                            {...field}
                                        >
                                            {config.stateList.map(option => (
                                                <MenuItem
                                                    key={option.code}
                                                    value={option.code}
                                                    disabled={option.disabled}
                                                >
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors?.billingState?.message && (
                                            <FormHelperText className="error-helper-text">
                                                {errors?.billingState?.message}
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                )}
                            />
                        </div>
                    </fieldset>
                )}

                <div>
                    <button
                        data-element-id="PaymentMethods_Add"
                        className={`primary-button button-size-medium ${addPaymentMethodRequestStatus === 'LOADING' ? 'loading' : ''
                            }`}
                        type="submit"
                    >
                        {getLabelFallback('ADDRESSES_ADDRESS_FORM_SUBMIT_CTA_LABEL')}
                        {addPaymentMethodRequestStatus === 'LOADING' && <PreLoader />}
                    </button>
                </div>
            </form>
        </div>
    );
};
export default EditAddressForm;
